/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * MeteoFWI — Pannello meteo integrato con calcolo FWI (Canadian Forest Service)
 *
 * Fonti dati:
 *   - Meteo: Open-Meteo API (gratuita, no API key, open-source)
 *     https://open-meteo.com
 *   - FWI: Canadian Forest Fire Weather Index System (Van Wagner 1985)
 *     Implementato localmente — nessuna dipendenza esterna
 *   - Classificazione: EFFIS (European Forest Fire Information System, JRC Ispra)
 *     6 classi: Very Low / Low / Medium / High / Very High / Extreme / Very Extreme
 *
 * Funzionamento:
 *   1. Prende le coordinate dal COAU (punto intervento) o dal GPS del tablet
 *   2. Chiama Open-Meteo per temperatura, umidità, vento, precipitazioni
 *   3. Calcola FWI con formula canadese
 *   4. Mostra classe di rischio EFFIS con allerta visiva
 *   5. Logga nel Diario Operativo
 */
// src/components/Meteo/MeteoFWI.tsx
import React, { useState, useCallback } from 'react';
import { useAppStore } from '../../store/useAppStore';

// ================================================================
// TIPI
// ================================================================

interface DatiMeteo {
  temperatura: number;        // °C
  umidita: number;            // %
  vento: number;              // km/h
  direzioneVento: number;     // gradi
  precipitazioni: number;     // mm/24h
  aggiornato: string;         // ora aggiornamento
  fonte: string;              // coordinate usate
}

interface DatiFWI {
  ffmc: number;   // Fine Fuel Moisture Code
  dmc: number;    // Duff Moisture Code
  dc: number;     // Drought Code
  isi: number;    // Initial Spread Index
  bui: number;    // Build Up Index
  fwi: number;    // Fire Weather Index
  classe: ClasseFWI;
}

type ClasseFWI = 'very_low' | 'low' | 'medium' | 'high' | 'very_high' | 'extreme' | 'very_extreme';

// ================================================================
// CLASSIFICAZIONE EFFIS
// ================================================================

const CLASSI_EFFIS: Record<ClasseFWI, {
  label: string; colore: string; sfondo: string;
  icona: string; soglia: number; azione: string;
}> = {
  very_low:     { label: 'Molto Basso',  colore: '#22c55e', sfondo: '#14532d', icona: '🟢', soglia: 0,  azione: 'Rischio trascurabile' },
  low:          { label: 'Basso',        colore: '#84cc16', sfondo: '#1a2e05', icona: '🟡', soglia: 5,  azione: 'Monitoraggio ordinario' },
  medium:       { label: 'Medio',        colore: '#f59e0b', sfondo: '#451a03', icona: '🟠', soglia: 15, azione: 'Attenzione — sorveglianza attiva' },
  high:         { label: 'Alto',         colore: '#f97316', sfondo: '#431407', icona: '🔴', soglia: 30, azione: 'Pericolo — attivare squadre' },
  very_high:    { label: 'Molto Alto',   colore: '#ef4444', sfondo: '#450a0a', icona: '🚨', soglia: 45, azione: 'Pericolo elevato — massima allerta' },
  extreme:      { label: 'Estremo',      colore: '#dc2626', sfondo: '#3b0764', icona: '⛔', soglia: 70, azione: 'ESTREMO — operazioni ad alto rischio' },
  very_extreme: { label: 'Molto Estremo',colore: '#7c3aed', sfondo: '#2e1065', icona: '💀', soglia: 100,azione: 'CRITICO — valutare sospensione operazioni' },
};

function classificaFWI(fwi: number): ClasseFWI {
  if (fwi >= 100) return 'very_extreme';
  if (fwi >= 70)  return 'extreme';
  if (fwi >= 45)  return 'very_high';
  if (fwi >= 30)  return 'high';
  if (fwi >= 15)  return 'medium';
  if (fwi >= 5)   return 'low';
  return 'very_low';
}

// Direzione vento in testo
function direzioneVentoTesto(gradi: number): string {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSO','SO','OSO','O','ONO','NO','NNO'];
  return dirs[Math.round(gradi / 22.5) % 16];
}

// ================================================================
// CALCOLO FWI CANADESE (Van Wagner 1985 — semplificato diurno)
// Input: temperatura (°C), umidità relativa (%), vento (km/h), pioggia (mm/24h)
// Output: FFMC, DMC, DC, ISI, BUI, FWI
//
// Nota: il FWI rigoroso richiede valori accumulati nei giorni precedenti
// (FFMC, DMC, DC del giorno prima). In assenza di storico, DOS518 usa
// valori standard di inizio stagione come da EFFIS documentation.
// ================================================================

function calcolaFWI(t: number, h: number, w: number, r: number): DatiFWI {
  // Valori iniziali standard estate mediterranea (da EFFIS)
  const ffmc0 = 85;
  const dmc0  = 6;
  const dc0   = 15;

  // --- FFMC (Fine Fuel Moisture Code) ---
  const mo = 147.2 * (101 - ffmc0) / (59.5 + ffmc0);
  const rf = r > 0.5 ? r - 0.5 : 0;
  let mr = mo;
  if (rf > 0) {
    const mr1 = mo + 42.5 * rf * Math.exp(-100 / (251 - mo)) * (1 - Math.exp(-6.93 / rf));
    mr = mo > 150 ? mr1 + 0.0015 * Math.pow(mo - 150, 2) * Math.pow(rf, 0.5) : mr1;
    if (mr > 250) mr = 250;
  }
  const ed = 0.942 * Math.pow(h, 0.679) + 11 * Math.exp((h - 100) / 10) + 0.18 * (21.1 - t) * (1 - Math.exp(-0.115 * h));
  const ew = 0.618 * Math.pow(h, 0.753) + 10 * Math.exp((h - 100) / 10) + 0.18 * (21.1 - t) * (1 - Math.exp(-0.115 * h));
  let m: number;
  if (mr > ed) {
    const ko = 0.424 * (1 - Math.pow(h / 100, 1.7)) + 0.0694 * Math.sqrt(w) * (1 - Math.pow(h / 100, 8));
    const kd = ko * 0.581 * Math.exp(0.0365 * t);
    m = ed + (mr - ed) * Math.pow(10, -kd);
  } else if (mr < ew) {
    const kl = 0.424 * (1 - Math.pow((100 - h) / 100, 1.7)) + 0.0694 * Math.sqrt(w) * (1 - Math.pow((100 - h) / 100, 8));
    const kw = kl * 0.581 * Math.exp(0.0365 * t);
    m = ew - (ew - mr) * Math.pow(10, -kw);
  } else {
    m = mr;
  }
  const ffmc = 59.5 * (250 - m) / (147.2 + m);

  // --- DMC (Duff Moisture Code) ---
  const re = r > 1.5 ? 0.92 * r - 1.27 : 0;
  const mo2 = 20 + Math.exp(5.6348 - dmc0 / 43.43);
  let pr = dmc0;
  if (re > 0) {
    const b = dmc0 <= 33 ? 100 / (0.5 + 0.3 * dmc0)
            : dmc0 <= 65 ? 14 - 1.3 * Math.log(dmc0)
            : 6.2 * Math.log(dmc0) - 17.2;
    const mr2 = mo2 + 1000 * re / (48.77 + b * re);
    pr = 43.43 * (5.6348 - Math.log(mr2 - 20));
  }
  const le = [6.5, 7.5, 9, 12.8, 13.9, 13.9, 12.4, 10.9, 9.4, 8, 7, 6][new Date().getMonth()];
  const k = Math.max(0, 1.894 * (t + 1.1) * (100 - h) * le * 0.0001);
  const dmc = pr + 100 * k;

  // --- DC (Drought Code) ---
  const rd = r > 2.8 ? 0.83 * r - 1.27 : 0;
  const qo = 800 * Math.exp(-dc0 / 400);
  const qr = rd > 0 ? qo + 3.937 * rd : qo;
  const dr = rd > 0 ? 400 * Math.log(800 / qr) : dc0;
  const lf = [-1.6, -1.6, -1.6, 0.9, 3.8, 5.8, 6.4, 5, 2.4, 0.4, -1.6, -1.6][new Date().getMonth()];
  const v = Math.max(0, 0.36 * (t + 2.8) + lf);
  const dc = dr + 0.5 * v;

  // --- ISI (Initial Spread Index) ---
  const fw = Math.exp(0.05039 * w);
  const fm = 147.2 * (101 - ffmc) / (59.5 + ffmc);
  const ff = 91.9 * Math.exp(-0.1386 * fm) * (1 + Math.pow(fm, 5.31) / 49300000);
  const isi = 0.208 * fw * ff;

  // --- BUI (Build Up Index) ---
  const bui = dmc <= 0.4 * dc
    ? 0.8 * dmc * dc / (dmc + 0.4 * dc)
    : dmc - (1 - 0.8 * dc / (dmc + 0.4 * dc)) * (0.92 + Math.pow(0.0114 * dmc, 1.7));

  // --- FWI (Fire Weather Index) ---
  const fd = bui < 80
    ? 0.626 * Math.pow(bui, 0.809) + 2
    : 1000 / (25 + 108.64 * Math.exp(-0.023 * bui));
  const b = 0.1 * isi * fd;
  const fwi = b > 1 ? Math.exp(2.72 * Math.pow(0.434 * Math.log(b), 0.647)) : b;

  return {
    ffmc: Math.round(ffmc * 10) / 10,
    dmc:  Math.round(dmc  * 10) / 10,
    dc:   Math.round(dc   * 10) / 10,
    isi:  Math.round(isi  * 10) / 10,
    bui:  Math.round(bui  * 10) / 10,
    fwi:  Math.round(fwi  * 10) / 10,
    classe: classificaFWI(fwi),
  };
}

// ================================================================
// COMPONENTE
// ================================================================

export const MeteoFWI: React.FC = () => {
  const coau = useAppStore((s) => s.coau);
  const addVoceDiario = useAppStore((s) => s.addVoceDiario);

  const [meteo, setMeteo] = useState<DatiMeteo | null>(null);
  const [fwi, setFwi] = useState<DatiFWI | null>(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [mostraDettagli, setMostraDettagli] = useState(false);

  // Recupera coordinate: prima dal COAU, poi GPS tablet
  const ottieniCoordinate = useCallback((): Promise<{ lat: number; lon: number; fonte: string }> => {
    return new Promise((resolve, reject) => {
      // Prova coordinate COAU
      const lat = parseFloat((coau as any)?.latitudine);
      const lon = parseFloat((coau as any)?.longitudine);
      if (!isNaN(lat) && !isNaN(lon)) {
        resolve({ lat, lon, fonte: `COAU (${lat.toFixed(4)}, ${lon.toFixed(4)})` });
        return;
      }
      // Fallback GPS tablet
      if (!navigator.geolocation) {
        reject(new Error('GPS non disponibile e coordinate COAU assenti'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          fonte: `GPS tablet (${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)})`,
        }),
        (err) => reject(new Error('Impossibile ottenere coordinate: ' + err.message)),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });
  }, [coau]);

  const aggiorna = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const { lat, lon, fonte } = await ottieniCoordinate();

      // Chiamata Open-Meteo — no API key, gratuita
      const url = `https://api.open-meteo.com/v1/forecast`
        + `?latitude=${lat}&longitude=${lon}`
        + `&current=temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,precipitation`
        + `&wind_speed_unit=kmh`
        + `&timezone=Europe%2FRome`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Open-Meteo errore: ${res.status}`);
      const json = await res.json();
      const c = json.current;

      const datiMeteo: DatiMeteo = {
        temperatura:     Math.round(c.temperature_2m * 10) / 10,
        umidita:         Math.round(c.relative_humidity_2m),
        vento:           Math.round(c.wind_speed_10m * 10) / 10,
        direzioneVento:  Math.round(c.wind_direction_10m),
        precipitazioni:  Math.round(c.precipitation * 10) / 10,
        aggiornato:      new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
        fonte,
      };

      const datiFWI = calcolaFWI(
        datiMeteo.temperatura,
        datiMeteo.umidita,
        datiMeteo.vento,
        datiMeteo.precipitazioni
      );

      setMeteo(datiMeteo);
      setFwi(datiFWI);

      // Log nel diario operativo
      const cls = CLASSI_EFFIS[datiFWI.classe];
      addVoceDiario(
        datiFWI.fwi >= 45 ? 'allerta' : 'sistema',
        cls.icona,
        `Meteo aggiornato — FWI: ${datiFWI.fwi} (${cls.label}) | T:${datiMeteo.temperatura}°C H:${datiMeteo.umidita}% V:${datiMeteo.vento}km/h`,
        { fwi: datiFWI.fwi, classe: datiFWI.classe, ...datiMeteo }
      );

    } catch (err: any) {
      setErrore(err.message || 'Errore durante l\'aggiornamento meteo');
    } finally {
      setLoading(false);
    }
  }, [ottieniCoordinate, addVoceDiario]);

  const cls = fwi ? CLASSI_EFFIS[fwi.classe] : null;

  return (
    <div style={{ padding: '16px', maxWidth: '700px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, color: '#f8fafc' }}>
          🌡️ Meteo & FWI — Rischio Incendio
        </h3>
        <button
          onClick={aggiorna}
          disabled={loading}
          style={{
            background: loading ? '#1e293b' : '#1e3a5f',
            color: loading ? '#475569' : '#93c5fd',
            border: `1px solid ${loading ? '#334155' : '#2563eb'}`,
            borderRadius: '8px', padding: '8px 16px',
            cursor: loading ? 'wait' : 'pointer',
            fontWeight: 700, fontSize: '0.9rem',
            transition: 'all 0.2s',
          }}
        >
          {loading ? '⏳ Aggiornamento...' : '🔄 Aggiorna'}
        </button>
      </div>

      {/* Fonte coordinate */}
      <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 12px' }}>
        {coau && (coau as any).latitudine
          ? `📍 Fonte: coordinate COAU (${(coau as any).latitudine}, ${(coau as any).longitudine})`
          : '📱 Nessuna coordinata nel COAU — userà GPS tablet'}
      </p>

      {errore && (
        <div style={{
          background: '#450a0a', border: '1px solid #dc2626',
          borderRadius: '8px', padding: '12px', marginBottom: '12px',
          color: '#fca5a5', fontSize: '0.9rem',
        }}>
          ⚠️ {errore}
        </div>
      )}

      {/* Stato iniziale */}
      {!meteo && !loading && !errore && (
        <div style={{
          background: '#0f172a', border: '1px dashed #334155',
          borderRadius: '12px', padding: '40px',
          textAlign: 'center', color: '#475569',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🌤️</div>
          <p>Premi <strong style={{ color: '#60a5fa' }}>Aggiorna</strong> per ottenere i dati meteo<br />
            e calcolare l'indice FWI per il punto intervento</p>
        </div>
      )}

      {/* Dati meteo + FWI */}
      {meteo && fwi && cls && (
        <>
          {/* Classe FWI — banner principale */}
          <div style={{
            background: cls.sfondo,
            border: `3px solid ${cls.colore}`,
            borderRadius: '14px', padding: '20px',
            marginBottom: '16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '6px' }}>{cls.icona}</div>
            <div style={{ color: cls.colore, fontSize: '2rem', fontWeight: 900, letterSpacing: '1px' }}>
              FWI: {fwi.fwi}
            </div>
            <div style={{ color: cls.colore, fontSize: '1.3rem', fontWeight: 700, marginBottom: '8px' }}>
              {cls.label.toUpperCase()}
            </div>
            <div style={{ color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 500 }}>
              {cls.azione}
            </div>
            <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '8px' }}>
              Aggiornato alle {meteo.aggiornato} · {meteo.fonte}
            </div>
          </div>

          {/* Parametri meteo */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px', marginBottom: '12px',
          }}>
            {[
              { icona: '🌡️', label: 'Temperatura', valore: `${meteo.temperatura} °C`,
                allerta: meteo.temperatura > 35 },
              { icona: '💧', label: 'Umidità relativa', valore: `${meteo.umidita} %`,
                allerta: meteo.umidita < 25 },
              { icona: '💨', label: 'Vento', valore: `${meteo.vento} km/h ${direzioneVentoTesto(meteo.direzioneVento)}`,
                allerta: meteo.vento > 30 },
              { icona: '🌧️', label: 'Precipitazioni 24h', valore: `${meteo.precipitazioni} mm`,
                allerta: false },
            ].map(({ icona, label, valore, allerta }) => (
              <div key={label} style={{
                background: allerta ? '#450a0a' : '#0f172a',
                border: `1px solid ${allerta ? '#dc2626' : '#1e293b'}`,
                borderRadius: '10px', padding: '12px',
              }}>
                <div style={{ fontSize: '1.4rem' }}>{icona}</div>
                <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>{label}</div>
                <div style={{
                  color: allerta ? '#fca5a5' : '#f1f5f9',
                  fontSize: '1.1rem', fontWeight: 700,
                }}>
                  {valore} {allerta && '⚠️'}
                </div>
              </div>
            ))}
          </div>

          {/* Barra scala FWI */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Scala FWI EFFIS</span>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>0 — 100+</span>
            </div>
            <div style={{
              height: '12px', borderRadius: '6px',
              background: 'linear-gradient(to right, #22c55e, #84cc16, #f59e0b, #f97316, #ef4444, #dc2626, #7c3aed)',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                left: `${Math.min(fwi.fwi, 110) / 110 * 100}%`,
                top: '-4px',
                transform: 'translateX(-50%)',
                width: '20px', height: '20px',
                background: cls.colore,
                borderRadius: '50%',
                border: '3px solid #fff',
                boxShadow: `0 0 8px ${cls.colore}`,
              }} />
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              color: '#475569', fontSize: '0.65rem', marginTop: '4px',
            }}>
              {['Molto Basso', 'Basso', 'Medio', 'Alto', 'Molto Alto', 'Estremo', 'V.Estremo'].map(l => (
                <span key={l}>{l}</span>
              ))}
            </div>
          </div>

          {/* Dettagli componenti FWI */}
          <button
            onClick={() => setMostraDettagli(!mostraDettagli)}
            style={{
              background: 'transparent', color: '#64748b',
              border: '1px solid #334155', borderRadius: '6px',
              padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem',
              width: '100%', marginBottom: mostraDettagli ? '10px' : 0,
            }}
          >
            {mostraDettagli ? '▲ Nascondi componenti FWI' : '▼ Mostra componenti FWI'}
          </button>

          {mostraDettagli && (
            <div style={{
              background: '#0f172a', border: '1px solid #1e293b',
              borderRadius: '10px', padding: '14px',
            }}>
              <p style={{ color: '#475569', fontSize: '0.75rem', margin: '0 0 10px' }}>
                Canadian Forest Fire Weather Index System (Van Wagner 1985)
              </p>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px',
              }}>
                {[
                  { label: 'FFMC', valore: fwi.ffmc, desc: 'Fine Fuel Moisture' },
                  { label: 'DMC',  valore: fwi.dmc,  desc: 'Duff Moisture' },
                  { label: 'DC',   valore: fwi.dc,   desc: 'Drought Code' },
                  { label: 'ISI',  valore: fwi.isi,  desc: 'Initial Spread Index' },
                  { label: 'BUI',  valore: fwi.bui,  desc: 'Build Up Index' },
                  { label: 'FWI',  valore: fwi.fwi,  desc: 'Fire Weather Index' },
                ].map(({ label, valore, desc }) => (
                  <div key={label} style={{
                    background: '#1e293b', borderRadius: '6px', padding: '8px', textAlign: 'center',
                  }}>
                    <div style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.9rem' }}>{label}</div>
                    <div style={{ color: '#f1f5f9', fontSize: '1.2rem', fontWeight: 900 }}>{valore}</div>
                    <div style={{ color: '#475569', fontSize: '0.65rem' }}>{desc}</div>
                  </div>
                ))}
              </div>
              <p style={{ color: '#334155', fontSize: '0.7rem', margin: '10px 0 0', textAlign: 'center' }}>
                Nota: calcolo istantaneo senza storico accumulato. Per FWI preciso usare serie temporale EFFIS.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
